import * as styles from "./MapPopover.css";

import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@frontend/components/atoms/Popover";
import { Skeleton } from "@frontend/components/atoms/Skeleton";
import { sizeToCss } from "@frontend/styles";
import { useLingui } from "@lingui/react/macro";

import { MapView } from "./MapView";
import type { MapPopoverProps } from "./MapView.types";
import { DEFAULT_POPOVER_HEIGHT, DEFAULT_POPOVER_WIDTH } from "./constants";

export function MapPopover({
  trigger,
  title,
  footer,
  ready = true,
  width = DEFAULT_POPOVER_WIDTH,
  height = DEFAULT_POPOVER_HEIGHT,
  side = "bottom",
  align,
  sideOffset,
  open,
  defaultOpen,
  onOpenChange,
  center,
  zoom,
  fit,
  mapId,
  interactive,
  controls,
  children,
}: MapPopoverProps): JSX.Element {
  const { t } = useLingui();

  return (
    <Popover open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger render={trigger} />

      <PopoverContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={styles.popup}
        data-slot="map-popover"
      >
        {title && (
          <div className={styles.header}>
            <PopoverTitle>{title}</PopoverTitle>
            <PopoverClose
              render={
                <Button
                  variant="ghost"
                  size="sm"
                  className={styles.close}
                  aria-label={t`Close`}
                  leftSection={<Icon name="X" size="sm" />}
                />
              }
            />
          </div>
        )}

        {ready ? (
          <MapView
            className={styles.map}
            style={{ width: sizeToCss(width) }}
            height={height}
            center={center}
            zoom={zoom}
            fit={fit}
            mapId={mapId}
            interactive={interactive}
            controls={controls}
          >
            {children}
          </MapView>
        ) : (
          <Skeleton
            className={styles.map}
            style={{ width: sizeToCss(width), height: sizeToCss(height) }}
          />
        )}

        {footer && (
          <div className={styles.footer} data-slot="map-popover-footer">
            {footer}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
